import ELK, { type ElkNode, type ElkExtendedEdge, type LayoutOptions as ElkOptions } from 'elkjs/lib/elk.bundled.js';
import type { Node as RFNode, Edge as RFEdge } from 'reactflow';
import { LinkType } from '@/types/link.types';

export type LayoutDirection = 'TB' | 'LR' | 'BT' | 'RL';

export type LayoutAlgorithm = 'layered' | 'mrtree' | 'stress' | 'force' | 'radial' | 'disco';

export const LAYOUT_ALGORITHMS: { value: LayoutAlgorithm; label: string; description: string }[] = [
  {
    value: 'layered',
    label: 'Hiérarchique (Sugiyama)',
    description: 'Couches alignées, idéal pour les graphes dirigés type organigramme.',
  },
  {
    value: 'mrtree',
    label: 'Arborescence',
    description: 'Arbre compact, optimal quand la plupart des liens sont de type Hiérarchie.',
  },
  {
    value: 'stress',
    label: 'Sémantique (stress)',
    description: 'Distances proportionnelles aux liaisons — convient aux graphes denses.',
  },
  {
    value: 'force',
    label: 'Force-directed',
    description: 'Simulation de ressorts, exploratoire — à privilégier pour les petits graphes.',
  },
  {
    value: 'radial',
    label: 'Radial',
    description: 'Cercles concentriques autour d\'un nœud racine.',
  },
  {
    value: 'disco',
    label: 'Composantes séparées',
    description: 'Applique un layout à chaque sous-graphe déconnecté puis les tasse.',
  },
];

interface LayoutOptions {
  algorithm?: LayoutAlgorithm;
  direction?: LayoutDirection;
  nodeWidth?: number;
  nodeHeight?: number;
  /** Distance between consecutive layers (between ranks). */
  layerSpacing?: number;
  /** Distance between two nodes in the same layer. */
  nodeSpacing?: number;
  /** Distance between a node and an incident edge. */
  edgeNodeSpacing?: number;
}

const DIRECTION_MAP: Record<LayoutDirection, 'DOWN' | 'RIGHT' | 'UP' | 'LEFT'> = {
  TB: 'DOWN',
  BT: 'UP',
  LR: 'RIGHT',
  RL: 'LEFT',
};

const elk = new ELK();

/**
 * Compute auto-layout positions using ELK (layered algorithm).
 *
 * Semantic edge weighting:
 *  - Hierarchy : strong weight (5), enforces parent→child rank progression.
 *  - Contribution : moderate weight (2), pulls A above B but less strictly.
 *  - Dependency (A depends on B) : reversed in layout — B should sit above A so
 *    the blocker is visually upstream of the blocked work.
 *  - Contradiction : weight 0 (informational), must not push two opposing nodes
 *    onto the same rank or drag one down below the other in a misleading way.
 *  - Complementarity / FreeForm : neutral weight (1).
 *
 * Group nodes (type === 'group') are excluded from the layout — their positions
 * are untouched. Orphans are packed into a side column by ELK's default
 * component placement.
 */
export async function computeAutoLayout(
  nodes: RFNode[],
  edges: RFEdge[],
  opts: LayoutOptions = {},
): Promise<Map<string, { x: number; y: number }>> {
  const {
    algorithm = 'layered',
    direction = 'TB',
    nodeWidth = 240,
    nodeHeight = 160,
    layerSpacing = 120,
    nodeSpacing = 80,
    edgeNodeSpacing = 40,
  } = opts;

  const flowNodes = nodes.filter((n) => n.type !== 'group');
  if (flowNodes.length === 0) return new Map();

  const layoutOptions: ElkOptions = {
    'elk.algorithm': algorithm,
    'elk.direction': DIRECTION_MAP[direction],
    'elk.spacing.nodeNode': String(nodeSpacing),
    'elk.spacing.edgeNode': String(edgeNodeSpacing),
    'elk.padding': '[top=40,left=40,bottom=40,right=40]',
    'elk.separateConnectedComponents': 'true',
  };

  if (algorithm === 'layered') {
    layoutOptions['elk.layered.spacing.nodeNodeBetweenLayers'] = String(layerSpacing);
    layoutOptions['elk.layered.nodePlacement.strategy'] = 'NETWORK_SIMPLEX';
    layoutOptions['elk.layered.crossingMinimization.strategy'] = 'LAYER_SWEEP';
    layoutOptions['elk.layered.cycleBreaking.strategy'] = 'GREEDY';
    layoutOptions['elk.layered.mergeEdges'] = 'true';
    layoutOptions['elk.edgeRouting'] = 'ORTHOGONAL';
  } else if (algorithm === 'mrtree') {
    layoutOptions['elk.mrtree.spacing.nodeNode'] = String(nodeSpacing);
  } else if (algorithm === 'force') {
    layoutOptions['elk.force.iterations'] = '300';
    layoutOptions['elk.force.repulsivePower'] = '0';
  } else if (algorithm === 'stress') {
    layoutOptions['elk.stress.desiredEdgeLength'] = String(layerSpacing * 2);
  } else if (algorithm === 'radial') {
    layoutOptions['elk.radial.radius'] = String(layerSpacing * 2);
  }

  const elkNodes: ElkNode[] = flowNodes.map((n) => ({
    id: n.id,
    width: n.width ?? nodeWidth,
    height: n.height ?? nodeHeight,
  }));

  const elkEdges: ElkExtendedEdge[] = [];
  const knownIds = new Set(flowNodes.map((n) => n.id));
  const applySemanticWeighting = algorithm === 'layered';

  for (const e of edges) {
    if (!knownIds.has(e.source) || !knownIds.has(e.target)) continue;
    const linkType = (e.data as { linkType?: LinkType } | undefined)?.linkType;

    let source = e.source;
    let target = e.target;
    let priority = 1;

    if (applySemanticWeighting) {
      switch (linkType) {
        case LinkType.Hierarchy:
          priority = 5;
          break;
        case LinkType.Contribution:
          priority = 2;
          break;
        case LinkType.Dependency:
          // A dépend de B → B doit être AU-DESSUS de A dans le layout.
          source = e.target;
          target = e.source;
          priority = 2;
          break;
        case LinkType.Contradiction:
          // Tension : ne contraint pas la hiérarchie visuelle.
          priority = 0;
          break;
        default:
          priority = 1;
      }
    }

    elkEdges.push({
      id: e.id,
      sources: [source],
      targets: [target],
      layoutOptions: applySemanticWeighting
        ? { 'elk.layered.priority.direction': String(priority) }
        : undefined,
    });
  }

  const graph: ElkNode = {
    id: 'root',
    layoutOptions,
    children: elkNodes,
    edges: elkEdges,
  };

  const laidOut = await elk.layout(graph);
  const positions = new Map<string, { x: number; y: number }>();
  for (const child of laidOut.children ?? []) {
    if (child.x === undefined || child.y === undefined) continue;
    positions.set(child.id, { x: child.x, y: child.y });
  }
  return positions;
}
